import type { Ref } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddRounded from '@mui/icons-material/AddRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';

export interface FeatureToggleRowProps {
  /** Feature name (translated or carrier-supplied). */
  label: string;
  /** Optional one-line description. */
  description?: string;
  /** Whether the feature is currently assigned to the line. */
  current: boolean;
  /** The desired end-state (checked). */
  on: boolean;
  onToggle: () => void;
  /** Status pill copy (translated). */
  addingLabel: string;
  removingLabel: string;
  currentlyOnLabel: string;
  currentlyOffLabel: string;
  disabled?: boolean;
  ref?: Ref<HTMLLabelElement>;
}

/**
 * One service-change feature row (the Polaris kit `ScFeatureRow`): a checkbox +
 * name/description, and a trailing status pill that reads the pending diff
 * relative to the line's current features — "Adding" (success) / "Removing"
 * (error) when changed, or "currently on/off" when unchanged.
 */
export function FeatureToggleRow({
  label,
  description,
  current,
  on,
  onToggle,
  addingLabel,
  removingLabel,
  currentlyOnLabel,
  currentlyOffLabel,
  disabled,
  ref,
}: FeatureToggleRowProps) {
  const theme = useTheme();
  const changed = on !== current;
  const tone = changed ? (on ? theme.palette.success : theme.palette.error) : null;

  return (
    <Box
      component="label"
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: '9px 14px',
        borderRadius: 1,
        cursor: disabled ? 'default' : 'pointer',
        bgcolor: tone ? alpha(tone.main, 0.05) : 'background.default',
        border: '1px solid',
        borderColor: tone ? alpha(tone.main, 0.4) : 'divider',
        transition: 'border-color 120ms, background 120ms',
      }}
    >
      <Checkbox
        size="small"
        checked={on}
        disabled={disabled}
        onChange={onToggle}
        sx={{ p: 0, color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{description}</Typography>
        )}
      </Box>
      {changed && tone ? (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            p: '3px 9px',
            borderRadius: 999,
            bgcolor: alpha(tone.main, 0.1),
            color: tone.dark,
            border: `1px solid ${alpha(tone.main, 0.3)}`,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {on ? <AddRounded sx={{ fontSize: 12 }} /> : <RemoveRounded sx={{ fontSize: 12 }} />}
          {on ? addingLabel : removingLabel}
        </Box>
      ) : (
        <Typography sx={{ flexShrink: 0, fontSize: 11, color: 'text.secondary' }}>
          {current ? currentlyOnLabel : currentlyOffLabel}
        </Typography>
      )}
    </Box>
  );
}
